/**
 * `/?at=39.0837,-94.5868[&alert=<cap id>]`: open that point, once.
 *
 * A link is somebody asking for a specific place, so it beats the default
 * location at boot, and a push notification (Stage 9.6) opens through one. It
 * is a query string rather than a hash because the service worker's
 * `navigateFallback` serves `index.html` for any navigation and keeps the
 * query.
 *
 * Everything in it arrives from outside the app, so it is validated here and
 * again wherever it is used: the coordinates must be finite and in range, and
 * the alert id must have the shape the backend's `CAP_ID_RE` checks
 * (`app/services/nws.py`), even though the backend checks it again before it
 * builds an upstream URL. A malformed link is ignored whole, with a warning,
 * and the boot carries on as if there were none.
 */

export interface DeepLink {
  lat: number
  lon: number
  /** A CAP alert id whose popup opens with the point. */
  alert?: string
}

/** The backend's `CAP_ID_RE`, the same shape to the character. */
export const CAP_ID_RE = /^urn:oid:[0-9A-Za-z.]{1,200}$/
/** A plain decimal: no exponent, no hex, no `Infinity`, no empty string. */
const DECIMAL_RE = /^-?\d{1,3}(\.\d{1,10})?$/

const PARAMS = ['at', 'alert'] as const

/** The link in `search` (`location.search`), or null when there is none or it is malformed. */
export function parseDeepLink(search: string): DeepLink | null {
  const params = new URLSearchParams(search)
  const at = params.get('at')
  const alert = params.get('alert')
  if (at === null && alert === null) return null

  const parts = (at ?? '').split(',')
  if (parts.length !== 2 || !parts.every((p) => DECIMAL_RE.test(p.trim()))) {
    return refuse(`the point ${JSON.stringify(at)} is not "lat,lon"`)
  }
  const [lat, lon] = parts.map((p) => Number(p.trim()))
  if (!(lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180)) {
    return refuse(`the point ${at} is out of range`)
  }
  if (alert !== null && !CAP_ID_RE.test(alert)) {
    return refuse('the alert id is not a CAP id')
  }
  return alert === null ? { lat, lon } : { lat, lon, alert }
}

/**
 * Read the link out of the address bar, then take it out of the address bar,
 * so a reload goes back to the default location rather than sticking to the
 * link forever. Any other query parameter is left alone.
 */
export function consumeDeepLink(): DeepLink | null {
  const url = new URL(window.location.href)
  if (!PARAMS.some((p) => url.searchParams.has(p))) return null
  const link = parseDeepLink(url.search)
  for (const p of PARAMS) url.searchParams.delete(p)
  try {
    history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`)
  } catch {
    // A sandboxed frame can refuse it. The link has still been read once.
  }
  return link
}

/**
 * A notification tapped while the app was already open (`public/push-sw.js`
 * posts `{type: 'fusionwx:open', url}`). The URL came out of a push payload,
 * so it goes through the same parser as the address bar, and only this
 * origin's links count.
 */
export function openMessageLink(data: unknown, origin: string): DeepLink | null {
  if (!data || typeof data !== 'object') return null
  const { type, url } = data as { type?: unknown; url?: unknown }
  if (type !== 'fusionwx:open' || typeof url !== 'string') return null
  let parsed: URL
  try {
    parsed = new URL(url, origin)
  } catch {
    return null
  }
  if (parsed.origin !== origin) return refuse('a notification pointed at another origin')
  return parseDeepLink(parsed.search)
}

function refuse(why: string): null {
  console.warn(`ignoring a malformed link: ${why}`)
  return null
}
